import React, { useCallback, useEffect, useRef } from 'react';
import LocationMapV2 from './v2/LocationMap';
import LocationMap from './LocationMap';
import type { Supplier } from '../../types/Supplier';
import { MARKET_RADIUS_MILES } from '../../seo/markets.mjs';

interface SearchMapContainerProps {
  /** Suppliers to display on the map */
  locations: Supplier[];
  /** Currently hovered supplier in the list */
  listHover: Supplier | null;
  /** Callback when a location marker is clicked */
  onLocationSelect: (location: Supplier) => void;
  /** Center position for the map */
  mapCenter: google.maps.LatLngLiteral;
  /** Whether to use the V2 map component */
  useMapV2: boolean;
  /** Callback when map bounds change */
  onBoundsChanged: (bounds: google.maps.LatLngBounds | undefined) => void;
  /** Callback when map bounds are initialized */
  onBoundsInitialized: (initialized: boolean) => void;
  /** Callback when the user starts dragging the map (V2 only) */
  onUserDragStart?: () => void;
  /** Whether the map is still at the zoom a search opened at (V2 only) */
  onAtSearchZoomChange?: (atSearchZoom: boolean) => void;
}

/**
 * Container component that encapsulates map rendering and bounds management:
 * - Switches between V2 and legacy map components
 * - Tracks bounds initialization state
 * - Handles bounds change callbacks
 *
 * This component focuses purely on map behavior logic, delegating
 * presentation concerns (positioning, visibility) to wrapper components.
 */
const SearchMapContainer = ({
  locations,
  listHover,
  onLocationSelect,
  mapCenter,
  useMapV2,
  onBoundsChanged,
  onBoundsInitialized,
  onUserDragStart,
  onAtSearchZoomChange
}: SearchMapContainerProps) => {
  const boundsInitializedRef = useRef(false);

  // Track previous map center values to avoid resetting bounds when
  // the center reference changes but lat/lng values remain the same
  // (e.g., re-searching the same city from autocomplete)
  const prevMapCenterRef = useRef<google.maps.LatLngLiteral | null>(null);

  // Reset initialization when map center changes (new search), then immediately
  // re-initialize if using legacy map (which doesn't support bounds tracking)
  useEffect(() => {
    const centerChanged = !prevMapCenterRef.current ||
      prevMapCenterRef.current.lat !== mapCenter.lat ||
      prevMapCenterRef.current.lng !== mapCenter.lng;

    if (!centerChanged) return;

    prevMapCenterRef.current = mapCenter;

    boundsInitializedRef.current = false;
    onBoundsInitialized(false);

    // For legacy map, immediately mark as initialized since it doesn't support bounds callbacks
    // This prevents infinite loading state while V2 map waits for bounds from MapBoundsReporter
    if (!useMapV2) {
      boundsInitializedRef.current = true;
      onBoundsInitialized(true);
    }
  }, [mapCenter, useMapV2, onBoundsInitialized]);

  // Handle bounds changes from map
  const handleBoundsChanged = useCallback((bounds: google.maps.LatLngBounds | null | undefined) => {
    if (bounds) {
      // Mark map bounds as initialized on first bounds report
      if (!boundsInitializedRef.current) {
        boundsInitializedRef.current = true;
        onBoundsInitialized(true);
      }

      // Always notify parent of bounds changes
      onBoundsChanged(bounds);
    }
  }, [onBoundsChanged, onBoundsInitialized]);

  return useMapV2 ? (
    <LocationMapV2
      locations={locations}
      listHover={listHover}
      onLocationSelect={onLocationSelect}
      defaultCenter={mapCenter}
      // A search opens on its market's radius (securspace-ui#3088).
      fitRadiusMiles={MARKET_RADIUS_MILES}
      onAtFittedZoomChange={onAtSearchZoomChange}
      onBoundsChanged={handleBoundsChanged}
      onUserDragStart={onUserDragStart}
    />
  ) : (
    <LocationMap
      onLocationSelect={onLocationSelect}
      locations={locations}
      listHover={listHover}
    />
  );
};

export default SearchMapContainer;

